import { FunctionComponent, useEffect } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { parseHtml } from '../Helper';
import languages from '../../Data/languages';
import SurveyType, { SurveyTypeValue } from '../../Data/SurveyType';
import { NextButton } from '../NextButton';
import { CheckboxRadioInput } from '../CheckboxRadioInput';
import type { Category, LegacyResponse, Template } from '../../types';

interface CommentPageProps {
  allowRatingChange?: boolean;
  toggleNextSection: () => void;
  template: Template;
  response: LegacyResponse;
  updateResponse: (key: string, value: unknown) => void;
  sendFinalResponse: (onSuccess?: () => void) => void;
  surveyType: SurveyTypeValue;
  showCategoryRequiredMessage?: boolean;
  sending?: boolean;
  questionFirst?: boolean;
  ratingPage: React.ReactNode;
}

export const CommentPage: FunctionComponent<CommentPageProps> = ({
  allowRatingChange,
  toggleNextSection,
  template,
  response,
  updateResponse,
  sendFinalResponse,
  surveyType,
  showCategoryRequiredMessage,
  sending,
  ratingPage
}) => {
  const appInsights = useAppInsightsContext();
  useEffect(() => {
    appInsights.trackPageView({ name: 'CommentPage.tsx' });
  }, [appInsights]);

  const language = template.language.name;
  const commentKey = surveyType === SurveyType.UNIVERSAL ? 'comment' : 'userComment';

  function setFollowUpText() {
    const rating = response.rating as number;
    if (rating < 7) {
      return template.followUpDetractor;
    }
    if (rating === 7 || rating === 8) {
      return template.followUpPassive;
    }
    if (rating > 8) {
      return template.followUpPromoter;
    }
  }

  const goToNextSection = template.enableFurtherQuestions && !template.questionFirst;

  function getSubmitButtonLabel() {
    if (sending) {
      return languages.SUBMITTING[language];
    }
    if (goToNextSection) {
      return languages.NEXT[language];
    }
    return languages.SUBMIT[language];
  }

  return (
    <>
      {allowRatingChange && ratingPage}
      <div className="body" style={allowRatingChange ? { marginTop: '10px' } : undefined}>
        <h2 className="question">{setFollowUpText()}</h2>
        <textarea
          rows={4}
          placeholder={languages.COMMENT_PLACEHOLDER[language]}
          onChange={(e) => {
            updateResponse(commentKey, e.target.value);
          }}
          value={response[commentKey] as string | undefined}
        ></textarea>
        {!template.hideCategories && (
          <>
            <h4>{template.categoryQuestion}</h4>
            {showCategoryRequiredMessage && (
              <span style={{ color: 'red', fontFamily: 'Verdana,sans-serif', fontStyle: 'italic' }}>
                {languages.REQUIRED_FIELD[language]}
              </span>
            )}
            <div className="categories">
              {template.categories?.map((c: Category) => {
                const existingCategory = response.category;
                const categoryAlreadySelected =
                  existingCategory &&
                  existingCategory.id === c.id &&
                  existingCategory.name === c.name;
                const selectCategory = () => updateResponse('category', c);
                return (
                  <button
                    key={c.id ?? c.name}
                    type="button"
                    style={{
                      background: c === response.category ? template.colorScheme : ''
                    }}
                    onClick={selectCategory}
                    className={categoryAlreadySelected ? 'category selected' : 'category'}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <br />
        {/* Universal campaign are always anonymous */}
        {surveyType !== SurveyType.UNIVERSAL && !template.hideAnonymity && (
          <>
            <div className="anonymity">
              <CheckboxRadioInput
                type="checkbox"
                defaultChecked={response.anon}
                name="anonymityCheckbox"
                question={parseHtml(languages.ANONYMOUS[language])}
                onClick={() => updateResponse('anon', !response.anon)}
              />
            </div>
            <br />
          </>
        )}
        {template.consentForPublicUse && template.consentForPublicUsePlacement === 1 && (
          <>
            <CheckboxRadioInput
              type="checkbox"
              defaultChecked={response.allowedForPublicUse}
              name="allowedForPublicUseCheckbox"
              question={parseHtml(template.consentForPublicUseText)}
              onClick={() => updateResponse('allowedForPublicUse', !response.allowedForPublicUse)}
            />
            <br />
          </>
        )}

        <div className="clearfix">
          <NextButton
            className="pull-right btn"
            onClick={() => {
              appInsights.trackEvent({ name: 'Commented', properties: response });
              if (goToNextSection) {
                toggleNextSection();
              } else {
                sendFinalResponse();
              }
            }}
            style={{ background: template.colorScheme }}
            disabled={sending}
            loading={sending}
          >
            {getSubmitButtonLabel()}
          </NextButton>
        </div>
      </div>
    </>
  );
};
